<template>
  <div class="crm-recipient-messages">
    <div v-if="loading && loadedItems.length === 0" class="py-3">
      <CSpinner color="primary" />
    </div>

    <template v-else>
      <CDataTable
        class="crm-recipient-messages-table"
        striped
        hover
        :items="loadedItems"
        :fields="computedFields"
        :loading="loading"
        :noItemsView="{
          noResults: 'No CRM messages found.',
          noItems: 'No CRM messages found.',
        }"
      >
        <template #message_id="{item}">
          <td>
            <strong>#{{ item.message_id }}</strong>
            <CBadge v-if="item.inbox" color="info" class="d-block mt-1">Inbox</CBadge>
          </td>
        </template>

        <template #title="{item}">
          <td>
            <strong>{{ displayTitle(item) }}</strong>
            <div class="text-muted small crm-message-preview">{{ item.short_message || '-' }}</div>
            <CBadge v-if="item.voucher_code" color="success" class="mt-1">
              {{ item.voucher_code }}
            </CBadge>
          </td>
        </template>

        <template #recipient="{item}">
          <td>
            <template v-if="isUserRecipient(item)">
              <CLink :to="{ name: 'User', params: { id: item.recipient.id } }">
                <strong>{{ displayRecipientName(item) }}</strong>
              </CLink>
              <div class="text-muted small">#{{ item.recipient.id }} {{ item.recipient.email || '' }}</div>
            </template>
            <template v-else-if="item.recipient">
              <strong>{{ displayRecipientName(item) }}</strong>
              <div class="text-muted small">Guest device #{{ item.recipient.id }}</div>
            </template>
            <span v-else>-</span>
          </td>
        </template>

        <template #segment="{item}">
          <td>{{ item.segment || '-' }}</td>
        </template>

        <template #channels="{item}">
          <td>
            <div class="crm-badge-row">
              <CBadge
                v-for="channel in splitChannels(item.channels)"
                :key="`${item.pivot_id}-${channel}`"
                color="light"
              >
                {{ channel }}
              </CBadge>
              <span v-if="splitChannels(item.channels).length === 0">-</span>
            </div>
          </td>
        </template>

        <template #status="{item}">
          <td>
            <div class="crm-badge-row">
              <CBadge :color="item.is_read ? 'success' : 'secondary'">Read</CBadge>
              <CBadge :color="item.notified ? 'success' : 'secondary'">Notified</CBadge>
              <CBadge :color="item.viewed ? 'success' : 'secondary'">Viewed</CBadge>
              <CBadge :color="item.clicked ? 'success' : 'secondary'">Clicked</CBadge>
            </div>
          </td>
        </template>

        <template #sent_at="{item}">
          <td>{{ formatDateTime(item.sent_at) }}</td>
        </template>

        <template #active_until="{item}">
          <td>{{ formatDateTime(item.active_until) }}</td>
        </template>
      </CDataTable>

      <div class="crm-recipient-messages-footer">
        <span>Total: <strong>{{ total }}</strong></span>
        <CPagination
          v-if="pages > 1"
          align="center"
          size="sm"
          :dots="false"
          :pages="pages"
          :active-page.sync="activePage"
        />
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CrmRecipientMessages',

  props: {
    url: {
      type: String,
      required: true,
    },
    itemsPerPageDefault: {
      type: Number,
      default: 10,
    },
    showRecipient: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadedItems: [],
      activePage: 1,
      pages: 1,
      total: 0,
      loading: false,
      requestId: 0,
    };
  },

  computed: {
    computedFields() {
      const fields = [
        { key: 'message_id', label: '#ID', _style: 'min-width: 86px; width: 86px;' },
        { key: 'title', label: 'Message', sorter: false, _style: 'min-width: 240px;' },
      ];

      if (this.showRecipient) {
        fields.push({ key: 'recipient', sorter: false, _style: 'min-width: 220px;' });
      }

      return fields.concat([
        { key: 'segment', _style: 'min-width: 130px;' },
        { key: 'channels', sorter: false, _style: 'min-width: 120px;' },
        { key: 'status', sorter: false, _style: 'min-width: 210px;' },
        { key: 'sent_at', label: 'Sent', _style: 'min-width: 160px;' },
        { key: 'active_until', label: 'Active until', _style: 'min-width: 160px;' },
      ]);
    },
  },

  watch: {
    url() {
      this.activePage = 1;
      this.getMessages();
    },
    activePage(value, oldValue) {
      if (value !== oldValue) {
        this.getMessages();
      }
    },
  },

  mounted() {
    this.getMessages();
  },

  methods: {
    async getMessages() {
      if (!this.url) {
        return;
      }

      const requestId = this.requestId + 1;
      const url = new URL(this.url);
      url.searchParams.append('page', this.activePage);
      url.searchParams.append('itemsPerPage', this.itemsPerPageDefault);

      this.requestId = requestId;
      this.loading = true;

      try {
        const { data } = await this.$axios.get(url);

        if (this.requestId !== requestId) {
          return;
        }

        this.loadedItems = data.data || [];
        this.activePage = data.current_page || 1;
        this.pages = data.last_page || 1;
        this.total = data.total || 0;
        this.$emit('loaded', data);
      } catch (error) {
        this.loadedItems = [];
        this.pages = 1;
        this.total = 0;
        this.$toast.error(error.response?.data?.message || 'Could not load CRM messages.');
      } finally {
        if (this.requestId === requestId) {
          this.loading = false;
        }
      }
    },
    displayTitle(item) {
      const title = item.title || {};
      const locale = item.recipient?.lang || 'nl';

      if (typeof title === 'string') {
        return title;
      }

      return title[locale] || title.nl || Object.values(title)[0] || `Message #${item.message_id}`;
    },
    splitChannels(channels) {
      return String(channels || '')
        .split(',')
        .map((channel) => channel.trim())
        .filter(Boolean);
    },
    isUserRecipient(item) {
      return item.recipient_type === 'user' && item.recipient?.id;
    },
    displayRecipientName(item) {
      if (!item.recipient) {
        return '-';
      }

      return item.recipient.name || `#${item.recipient.id}`;
    },
    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(value);
      return isNaN(date.getTime()) ? '-' : date.toLocaleString();
    },
  },
};
</script>

<style scoped>
.crm-recipient-messages-table {
  font-size: 0.875rem;
}

.crm-message-preview {
  max-width: 34rem;
  white-space: normal;
}

.crm-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.crm-recipient-messages-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.75rem;
}
</style>